import * as React from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * One option in the combobox dropdown. `value` is the canonical id sent to
 * the parent on selection; `label` is what the user sees.
 *
 * Optional adornments rendered on the right side of the row, in this order:
 *   - `badge` → primary-tinted pill, meant for "earns extra" markers (e.g.
 *     `+$2.50` niche bonus). Visually prominent — the prize-bearing rows
 *     should pop without needing the user to read the row first.
 *   - `hint`  → muted text. Use for secondary context: status counts, the
 *     ★ focus-niche marker, an email under a name, etc.
 */
export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
  badge?: string;
}

export interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Render an "All" / "None" sentinel at the top with this label. Selecting it emits `value=""`. */
  allowAll?: boolean;
  allLabel?: string;
  className?: string;
  /** Dropdown panel width — defaults to matching the trigger. */
  panelClassName?: string;
  loading?: boolean;
  /** id for the text input, so a <Label htmlFor> can point at it. */
  id?: string;
  'aria-label'?: string;
}

/**
 * Searchable combobox. Type to filter (substring, case-insensitive); click
 * to select. All filtering happens client-side — the parent passes the
 * complete options list once and we never re-fetch on keystroke.
 *
 * Ported from `spade-financial`'s EntityCombobox; same shape, generic
 * label/value pair instead of the entity-specific `_id`/`name`.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyText = 'No matches',
  disabled = false,
  allowAll = false,
  allLabel = 'All',
  className,
  panelClassName,
  loading = false,
  id,
  'aria-label': ariaLabel,
}: ComboboxProps): JSX.Element {
  const [searchValue, setSearchValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filteredOptions = React.useMemo(() => {
    if (!searchValue.trim()) return options;
    const needle = searchValue.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle),
    );
  }, [options, searchValue]);

  const items = React.useMemo<ComboboxOption[]>(() => {
    if (allowAll && !searchValue.trim()) {
      return [{ value: '', label: allLabel }, ...filteredOptions];
    }
    return filteredOptions;
  }, [allowAll, allLabel, filteredOptions, searchValue]);

  const selected = options.find((o) => o.value === value);
  const displayValue = selected?.label ?? (value === '' && allowAll ? allLabel : '');

  function handleSelect(next: string): void {
    onChange(next);
    setOpen(false);
    setSearchValue('');
  }

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(ev: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
        setSearchValue('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          aria-label={ariaLabel}
          type="text"
          autoComplete="off"
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 pr-8 text-sm transition-colors duration-150 ease-out-quart pointer-coarse:h-11 pointer-coarse:text-base',
            'placeholder:text-muted-foreground/70',
            'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          placeholder={placeholder}
          value={open ? searchValue : displayValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              setSearchValue('');
              inputRef.current?.blur();
            }
          }}
          disabled={disabled}
        />
        <button
          type="button"
          className="absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded hover:bg-muted"
          onClick={() => {
            setOpen((v) => !v);
            if (!open) inputRef.current?.focus();
          }}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Toggle dropdown"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground transition-transform duration-150',
              open && 'rotate-180',
            )}
          />
        </button>
      </div>

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-overlay',
            panelClassName,
          )}
        >
          <div className="max-h-[300px] overflow-auto p-1">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-2 py-6 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                {emptyText}
              </div>
            ) : (
              items.map((item) => {
                const isSelected = item.value === value;
                return (
                  <button
                    key={item.value || '__all__'}
                    type="button"
                    onClick={() => handleSelect(item.value)}
                    className={cn(
                      'flex w-full select-none items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent text-accent-foreground',
                      !item.value && allowAll && 'text-muted-foreground',
                    )}
                  >
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge && (
                      <span className="shrink-0 rounded-sm bg-brand/15 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-brand">
                        {item.badge}
                      </span>
                    )}
                    {item.hint && (
                      <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                        {item.hint}
                      </span>
                    )}
                    {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
